import axios from 'axios';
import { logout } from '../services/authService';
import { hasApiAccess } from '../services/privilegeService';
import API_CONFIG from '../config/api.config';

// Create axios instance with base URL
const axiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from localStorage using the correct key
    const token = localStorage.getItem('accessToken');
    
    // If token exists, add it to request headers
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Replace any hardcoded URLs with the configured base URL and path
    if (config.url && config.url.includes('juharitrading.com')) {
      // Extract the path portion of the URL
      const url = new URL(config.url);
      const path = url.pathname;
      // Remove any existing /api prefix since it will be added below
      const cleanPath = path.replace(/^\/api/, '');
      console.log(`Replacing hardcoded URL: ${config.url} with path: ${cleanPath}`);
      config.url = cleanPath;
    }
    
    // Check if URL is absolute (starts with http or https)
    const isAbsoluteUrl = /^https?:\/\//i.test(config.url);
    
    // No need to add API prefix since BASE_URL already includes /api
    // Just log the final URL for debugging
    if (!isAbsoluteUrl) {
      console.log(`Making API request to: ${API_CONFIG.BASE_URL}${config.url}`);
    }
    
    // Check if user has permission for this endpoint using the privilege service
    try {
      if (!hasApiAccess(config.url)) {
        console.log(`API access denied for ${config.url}`);
        // Reject the request if user doesn't have permission
        return Promise.reject({
          response: {
            status: 403,
            data: {
              message: 'You do not have permission to access this resource'
            }
          }
        });
      }
    } catch (error) {
      console.error('Error checking API access:', error);
      // If there's an error in the permission check, allow the request
      // to avoid blocking legitimate requests due to permission system errors
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401/403 errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status } = error.response;
      
      // Handle 401 Unauthorized
      if (status === 401) {
        // Token is invalid or expired, clear local storage and redirect to login
        logout();
        return Promise.reject(error);
      }
      
      // Handle 403 Forbidden
      if (status === 403) {
        console.error('Access forbidden:', error.response.data);
        // Don't redirect, let the component handle the error
        return Promise.reject(error);
      }

      // Handle 404 Not Found
      if (status === 404) {
        console.error('Resource not found:', error.config.url);
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance; 